import './App.css';
import { useState } from 'react';
import { Analytics } from '@vercel/analytics/react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
  useMediaQuery
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import StarIcon from '@mui/icons-material/Star';
import HearingIcon from '@mui/icons-material/Hearing';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import colocacionAudifono from './fotos/colocacion-audifono.jpg';
import audifonoPar from './fotos/audifono-par.jpg';
import audifonoSingle from './fotos/audifono-single.jpg';
import fotoPerfil from './fotos/foto-perfil.jpeg';

const theme = createTheme({
  palette: {
    primary: { main: '#1f5fbf' },
    secondary: { main: '#14b8a6' },
    background: { default: '#f6f8fc' }
  },
  typography: {
    fontFamily: '"Sora", "Nunito Sans", "Segoe UI", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 }
  }
});

const services = [
  {
    title: 'Evaluación Auditiva Completa',
    detailsSections: {
      h2: 'Qué incluye la evaluación',
      intro:
        'Durante la consulta realizamos pruebas audiológicas clínicas para analizar cómo está funcionando su sistema auditivo. Son seguras, no invasivas y completamente indoloras.',
      sections: [
        {
          h3: 'Audiometría clínica',
          body: 'Mide la capacidad de escuchar diferentes tonos y niveles de sonido para determinar si existe algún grado de pérdida auditiva.'
        },
        {
          h3: 'Timpanometría',
          body: 'Evalúa el funcionamiento del oído medio y la movilidad del tímpano para detectar alteraciones como presión anormal.'
        },
        {
          h3: 'Emisiones otoacústicas',
          body: 'Prueba objetiva que analiza la respuesta del oído interno, frecuente en tamizaje infantil y evaluaciones clínicas.'
        },
        {
          h3: 'Orientación y seguimiento profesional',
          body: 'Al finalizar, explicamos el estado de su audición y definimos recomendaciones y plan de seguimiento personalizado.'
        }
      ]
    },
    details: 'Información completa disponible en secciones dentro de este diálogo.',
    text: 'Valoración audiológica mediante pruebas clínicas especializadas para identificar alteraciones auditivas con precisión.',
    icon: <HearingIcon />
  },
  {
    title: 'Adaptación de Audífonos',
    detailsSections: {
      h2: 'Adaptación de audífonos',
      intro:
        'La adaptación de audífonos es un proceso personalizado que busca mejorar la audición y la comprensión del habla en la vida diaria.',
      sections: [
        {
          h3: 'Selección del audífono adecuado',
          body: 'Elegimos el tipo y modelo de audífono más apropiado según el grado de pérdida auditiva, las necesidades del paciente y su estilo de vida.'
        },
        {
          h3: 'Programación y calibración personalizada',
          body: 'Los audífonos se configuran digitalmente de acuerdo con los resultados de la evaluación auditiva para asegurar una amplificación precisa y cómoda.'
        },
        {
          h3: 'Orientación y entrenamiento en el uso',
          body: 'Explicamos el manejo, cuidado y mantenimiento de los audífonos para facilitar su adaptación en las actividades diarias.'
        },
        {
          h3: 'Seguimiento y ajustes',
          body: 'Realizamos controles periódicos para ajustar la programación y asegurar que el paciente obtenga el máximo beneficio auditivo.'
        }
      ],
      closing:
        'Nuestro objetivo es lograr una adaptación cómoda, natural y efectiva que mejore la comunicación y la calidad de vida.'
    },
    details: 'Información completa disponible en secciones dentro de este diálogo.',
    text: 'Selección, programación y calibración personalizada de audífonos según las necesidades auditivas y el estilo de vida de cada paciente. Se reciben recetas de la CCSS.',
    icon: <GraphicEqIcon />
  },
  {
    title: 'Venta de Audífonos',
    detailsSections: {
      h2: 'Venta y recomendación de audífonos',
      intro:
        'Ofrecemos audífonos para diferentes grados de pérdida auditiva, con asesoría personalizada para elegir una opción cómoda, funcional y acorde al estilo de vida del paciente.',
      sections: [
        {
          h3: 'Opciones según necesidad',
          body: 'Contamos con modelos para uso unilateral y bilateral, versiones discretas y equipos con mayor potencia para casos que requieren más soporte auditivo.'
        },
        {
          h3: 'Acompañamiento profesional',
          body: 'La compra se realiza con orientación clínica para que el paciente entienda diferencias, beneficios y cuidados de cada tipo de audífono.'
        },
        {
          h3: 'Configuración y seguimiento',
          body: 'Luego de la entrega realizamos ajuste inicial y recomendaciones de uso para facilitar la adaptación en conversaciones, trabajo y actividades diarias.'
        }
      ],
      closing:
        'Nuestro objetivo es que cada paciente salga con una solución auditiva funcional, cómoda y sostenible en el tiempo.'
    },
    details: 'Información completa disponible en secciones dentro de este diálogo.',
    text: 'Venta de audífonos con orientación profesional para elegir el modelo ideal según necesidad auditiva y estilo de vida.',
    icon: <GraphicEqIcon />,
    gallery: [audifonoPar, audifonoSingle]
  },
  {
    title: 'Tamizaje Auditivo',
    detailsSections: {
      h2: 'Tamizaje auditivo infantil',
      intro:
        'El tamizaje auditivo es una evaluación preventiva que permite detectar de forma temprana posibles alteraciones en la audición infantil. La detección oportuna es fundamental para el adecuado desarrollo del lenguaje, la comunicación y el aprendizaje.',
      sections: [
        {
          h3: 'Evaluación objetiva y segura',
          body: 'Durante esta evaluación utilizamos pruebas audiológicas objetivas y seguras, diseñadas especialmente para bebés y niños pequeños.'
        },
        {
          h3: 'Emisiones otoacústicas',
          body: 'Permiten evaluar la respuesta de las células del oído interno de manera rápida y no invasiva.'
        },
        {
          h3: 'Resultados y orientación a los padres',
          body: 'Una vez realizada la prueba, explicamos los resultados y brindamos las recomendaciones necesarias para el seguimiento auditivo del niño.'
        }
      ],
      closing:
        'Nuestro objetivo es detectar de manera temprana cualquier dificultad auditiva y acompañar el desarrollo saludable de la audición infantil.'
    },
    details: 'Información completa disponible en secciones dentro de este diálogo.',
    text: 'Revisión preventiva de la audición en niños mediante pruebas objetivas que permiten detectar alteraciones auditivas de forma temprana y oportuna.',
    icon: <RecordVoiceOverIcon />
  },
  {
    title: 'Planes de Conservación Auditiva Ocupacional',
    detailsSections: {
      h2: 'Planes de conservación auditiva ocupacional',
      intro:
        'Los planes de conservación auditiva están diseñados para prevenir la pérdida auditiva inducida por ruido en el entorno laboral.',
      sections: [
        {
          h3: 'Monitoreo de salud auditiva',
          body: 'Estos programas permiten monitorear la salud auditiva de los trabajadores con evaluaciones periódicas y seguimiento profesional.'
        },
        {
          h3: 'Cumplimiento en seguridad ocupacional',
          body: 'También ayudan a cumplir con las recomendaciones de seguridad ocupacional mediante acciones preventivas y trazabilidad de resultados.'
        }
      ]
    },
    details: 'Información completa disponible en secciones dentro de este diálogo.',
    text: 'Programas preventivos para proteger la audición de trabajadores expuestos a ruido y fortalecer la seguridad ocupacional.',
    icon: <WorkOutlineIcon />
  }
];
const team = [
  {
    name: 'Dr. Santiago Morales',
    role: 'Audiólogo',
    image: fotoPerfil,
    profileUrl: 'https://redauditiva.com/audiologo/santiago-jose-morales-cespedes/'
  },
];

const testimonials = [
  'Excelente atención y explicaciones claras. Salí con diagnóstico y plan en la misma cita.',
  'Me ayudaron con la adaptación de audífonos de forma muy humana y profesional.',
  'El seguimiento fue constante y mejoró mucho mi calidad de vida en pocas semanas.'
];

const navItems = [
  { label: 'Servicios', href: '#servicios' },
  { label: 'Pruebas', href: '#pruebas' },
  { label: 'Nuestro Equipo', href: '#equipo' },
  { label: 'Pacientes', href: '#pacientes' },
  { label: 'Ubicación', href: '#ubicacion' },
  { label: 'FAQ', href: '#faq' }
];

const WHATSAPP_URL = 'https://wa.me/50671217141';

function App() {
  const [selectedService, setSelectedService] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Analytics />
      <Box id="inicio" sx={{ bgcolor: 'background.default' }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            background: 'linear-gradient(90deg, #0f4ea8 0%, #1d6cd3 100%)'
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ py: 0.5, gap: 2 }}>
              <Typography
                variant="h6"
                component="a"
                href="#inicio"
                sx={{ fontWeight: 800, flexGrow: 1, color: 'inherit', textDecoration: 'none' }}
              >
                audinova
              </Typography>
              <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    color="inherit"
                    href={item.href}
                    sx={{ textTransform: 'none' }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
              <Button
                variant="contained"
                color="secondary"
                component="a"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
              >
                Agendar cita
              </Button>
              <IconButton
                color="inherit"
                component="a"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
              >
                <WhatsAppIcon />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>

        <Box
          sx={{
            background:
              'linear-gradient(90deg, #eef3fc 0%, #f8fbff 55%, rgba(255,255,255,0.6) 100%)',
            borderBottom: '1px solid #dbe6fa'
          }}
        >
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{ fontSize: { xs: 38, md: 56 }, color: '#164391', mb: 2 }}>
                  Escucha mejor, vive mejor.
                </Typography>
                <Typography sx={{ fontSize: 22, color: '#2b406a', mb: 3 }}>
                  Evaluación auditiva profesional, adaptación de audífonos y seguimiento personalizado, con consultas en el Hospital Cooperativo y otros puntos de atención.
                </Typography>
                <Stack spacing={1.2} sx={{ mb: 3 }}>
                  {['Adaptación de audífonos', 'Audiometría clínica', 'Timpanometría', 'Emisiones otoacústicas', 'Planes de conservación auditiva'].map(
                    (item) => (
                      <Stack key={item} direction="row" spacing={1.3} alignItems="center">
                        <CheckCircleIcon color="secondary" />
                        <Typography>{item}</Typography>
                      </Stack>
                    )
                  )}
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    component="a"
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ borderRadius: 3, textTransform: 'none' }}
                  >
                    Agendar evaluación
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    color="secondary"
                    component="a"
                    href="#servicios"
                    sx={{ borderRadius: 3, textTransform: 'none' }}
                  >
                    Ver servicios
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#183e7f', mb: 1 }}>
                  <StarIcon sx={{ color: '#f4b400' }} />
                  <Typography sx={{ fontWeight: 700 }}>4.8/5 en reseñas</Typography>
                  <Typography>|</Typography>
                  <Typography sx={{ fontWeight: 700 }}>10 años de experiencia</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  {['Audiometría', 'Impedanciometría', 'OAE'].map((x) => (
                    <Chip key={x} label={x} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src={colocacionAudifono}
                  alt="Consulta auditiva"
                  sx={{
                    width: '100%',
                    height: { xs: 320, md: 470 },
                    objectFit: 'cover',
                    borderRadius: 5,
                    boxShadow: '0 25px 50px rgba(16, 54, 118, 0.2)'
                  }}
                />
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container id="servicios" maxWidth="lg" sx={{ py: 7, scrollMarginTop: '96px' }}>
          <SectionTitle title="Servicios de Audiología y Audífonos" />
          <Stack spacing={3}>
            {services.map((service) => (
              <Card key={service.title} sx={{ width: '100%', borderRadius: 3, border: '1px solid #dce6f7' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, color: 'primary.main' }}>
                      {service.icon}
                      <Typography variant="h6">{service.title}</Typography>
                    </Stack>
                    <Typography sx={{ mb: 2, color: 'text.secondary' }}>{service.text}</Typography>
                    {service.gallery ? (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mb: 2 }}>
                        {service.gallery.map((img, idx) => (
                          <Box
                            key={`${service.title}-img-${idx}`}
                            component="img"
                            src={img}
                            alt={`${service.title} ${idx + 1}`}
                            sx={{
                              width: '100%',
                              maxWidth: { xs: '100%', sm: 210 },
                              height: 120,
                              objectFit: 'cover',
                              borderRadius: 2,
                              border: '1px solid #d9e3f5'
                            }}
                          />
                        ))}
                      </Stack>
                    ) : null}
                    <Button sx={{ textTransform: 'none', p: 0 }} onClick={() => setSelectedService(service)}>
                      Conocer más
                    </Button>
                  </CardContent>
                </Card>
            ))}
          </Stack>
        </Container>

        <Dialog
          open={Boolean(selectedService)}
          onClose={() => setSelectedService(null)}
          fullScreen={isMobile}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>{selectedService?.title}</DialogTitle>
          <DialogContent dividers>
            {selectedService?.detailsSections ? (
              <Stack spacing={2}>
                {selectedService.gallery ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                    {selectedService.gallery.map((img, idx) => (
                      <Box
                        key={`dialog-img-${idx}`}
                        component="img"
                        src={img}
                        alt={`${selectedService.title} ${idx + 1}`}
                        sx={{
                          width: '100%',
                          maxWidth: { xs: '100%', sm: 260 },
                          height: 150,
                          objectFit: 'cover',
                          borderRadius: 2,
                          border: '1px solid #d9e3f5'
                        }}
                      />
                    ))}
                  </Stack>
                ) : null}
                <Typography component="h2" variant="h5" sx={{ color: '#153f86' }}>
                  {selectedService.detailsSections.h2}
                </Typography>
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  {selectedService.detailsSections.intro}
                </Typography>
                {selectedService.detailsSections.sections.map((section) => (
                  <Box key={section.h3}>
                    <Typography component="h3" variant="h6" sx={{ mb: 0.7 }}>
                      {section.h3}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                      {section.body}
                    </Typography>
                  </Box>
                ))}
                {selectedService.detailsSections.closing ? (
                  <Typography sx={{ color: 'text.secondary', lineHeight: 1.8, fontWeight: 600 }}>
                    {selectedService.detailsSections.closing}
                  </Typography>
                ) : null}
              </Stack>
            ) : (
              <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                {selectedService?.details}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setSelectedService(null)} sx={{ textTransform: 'none' }}>
              Cerrar
            </Button>
            <Button
              variant="contained"
              component="a"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              sx={{ textTransform: 'none' }}
            >
              Agendar cita
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          id="pruebas"
          sx={{
            py: 6,
            bgcolor: '#f2f6fd',
            borderTop: '1px solid #dce5f8',
            borderBottom: '1px solid #dce5f8',
            scrollMarginTop: '96px'
          }}
        >
          <Container maxWidth="lg">
            <SectionTitle title="Proceso de Evaluación Auditiva" />
            <Grid container spacing={3}>
              {[
                ['1', 'Agenda fácilmente'],
                ['2', 'Evaluamos tu audición'],
                ['3', 'Plan Personalizado']
              ].map((item) => (
                <Grid item xs={12} md={4} key={item[0]}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Chip label={item[0]} color="primary" />
                        <Typography variant="h6">{item[1]}</Typography>
                      </Stack>
                      <Typography sx={{ mt: 1.5, color: 'text.secondary' }}>
                        Proceso guiado de principio a fin con claridad en tiempos y resultados.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Container id="equipo" maxWidth="lg" sx={{ py: 7, scrollMarginTop: '96px' }}>
          <SectionTitle title="Especialistas en Audiología" />
          <Grid container spacing={3}>
            {team.map((member) => (
              <Grid item xs={12} md={4} key={member.name}>
                <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                  <Box component="img" src={member.image} alt={member.name} sx={{ width: '100%', height: 250, objectFit: 'cover' }} />
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#153f86' }}>
                      {member.name}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', mb: 2 }}>{member.role}</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      component={member.profileUrl ? 'a' : 'button'}
                      href={member.profileUrl}
                      target={member.profileUrl ? '_blank' : undefined}
                      rel={member.profileUrl ? 'noreferrer' : undefined}
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      Ver perfil
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Box
          id="pacientes"
          sx={{ py: 7, bgcolor: '#f2f6fd', borderTop: '1px solid #dce5f8', scrollMarginTop: '96px' }}
        >
          <Container maxWidth="lg">
            <SectionTitle title="Opiniones de Pacientes" />
            <Grid container spacing={3}>
              {testimonials.map((quote) => (
                <Grid item xs={12} md={4} key={quote}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography sx={{ color: 'text.secondary' }}>"{quote}"</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 4 }}>
              {['+100 Pacientes Atendidos', 'Seguimiento personalizado', 'Atención integral'].map(
                (item) => (
                  <Chip key={item} label={item} color="primary" variant="outlined" />
                )
              )}
            </Stack>
          </Container>
        </Box>

        <Container id="faq" maxWidth="lg" sx={{ py: 7, scrollMarginTop: '96px' }}>
          <SectionTitle title="Preguntas frecuentes sobre audición y audífonos" />
          <Stack spacing={1.5}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>¿Cuánto dura una evaluación auditiva?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: 'text.secondary' }}>
                  Normalmente entre 45 y 60 minutos, incluyendo entrevista y explicación de
                  resultados.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>¿Entregan resultados el mismo día?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: 'text.secondary' }}>
                  Sí, en la mayoría de pruebas clínicas te entregamos un resumen claro durante la
                  misma cita.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>¿Atienden niños y adultos?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: 'text.secondary' }}>
                  Sí, contamos con protocolos para niños, adultos y evaluaciones ocupacionales.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Container>

        <Box
          id="ubicacion"
          sx={{
            background: 'linear-gradient(90deg, #0e4da7 0%, #1b66c7 60%, #1db6a7 100%)',
            color: 'white',
            py: 5,
            scrollMarginTop: '96px'
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    width: '100%',
                    height: 180,
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.25)'
                  }}
                >
                  <Box
                    component="iframe"
                    title="Ubicación Audinova"
                    src="https://www.google.com/maps?q=Hospital+Cooperativo,+Ciudad+Quesada,+Costa+Rica&output=embed"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    sx={{
                      width: '100%',
                      height: '100%',
                      border: 0
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="h4" component="h2" sx={{ mb: 1 }}>
                  Agenda tu evaluación auditiva en Ciudad Quesada
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    color="secondary"
                    component="a"
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ borderRadius: 3, textTransform: 'none' }}
                  >
                    Agendar cita
                  </Button>
                  <Button
                    variant="contained"
                    component="a"
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ bgcolor: '#16a34a', borderRadius: 3, textTransform: 'none' }}
                  >
                    Escribir por WhatsApp
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 2.5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <PlaceIcon fontSize="small" color="primary" />
              <Typography variant="body2">Hospital Cooperativo, Ciudad Quesada, Costa Rica</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon fontSize="small" color="primary" />
              <Typography variant="body2">Lun a Sab 8:00 am - 5:00 pm</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <LocalPhoneIcon fontSize="small" color="primary" />
              <Typography variant="body2">+506 7121 7141</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <LocalPhoneIcon fontSize="small" color="primary" />
              <Typography variant="body2">+506 2460 1080</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmailIcon fontSize="small" color="primary" />
              <Typography variant="body2">info@audinova.com</Typography>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
            <Button
              href="https://maps.app.goo.gl/wPkBkZgfxFQxm9CQA"
              target="_blank"
              rel="noreferrer"
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Abrir en Google Maps
            </Button>
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={2} sx={{ justifyContent: 'center', flexWrap: 'wrap' }}>
            {['Servicios', 'Pruebas', 'Equipo', 'Aviso de Privacidad', 'Términos y Condiciones'].map(
              (item) => (
                <Typography key={item} variant="body2" sx={{ color: '#27549c' }}>
                  {item}
                </Typography>
              )
            )}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

function SectionTitle({ title }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3.5 }}>
      <Divider sx={{ flex: 1 }} />
      <Typography variant="h3" sx={{ color: '#1a4d9c', fontSize: { xs: 30, md: 44 } }}>
        {title}
      </Typography>
      <Divider sx={{ flex: 1 }} />
    </Stack>
  );
}

export default App;

